import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { useCurrentSpaceId } from '@/entities/teamspace';

import { API } from '@/shared/api';
import { Settings } from '@/shared/types/Settings';

import { defaultSettings } from '../lib/defaultSettings';
import { settingsAdapter } from '../lib/settingsAdapter';
import { USER_QUERY_KEYS } from '../lib/userQueryKeys';
import { useCurrentUser, useIsAuthenticated } from './useCurrentUser';

const fetchSettings = async (isAuthenticated: boolean, spaceId: string): Promise<Settings> => {
  if (spaceId) {
    const response = await API.getSpaceSettings(spaceId);
    return settingsAdapter(response);
  }

  const settingsApi = isAuthenticated ? API.getUserSettings : API.getAppSettings;
  const response = await settingsApi();

  return settingsAdapter(response);
};

export const useSettings = (): Settings => {
  const isAuthenticated = useIsAuthenticated();
  const spaceId = useCurrentSpaceId();
  const isSuperUser = useCurrentUser((s) => s.is_superuser);

  const query = useQuery<Settings>({
    queryFn: () => fetchSettings(isAuthenticated, isSuperUser.data ? '' : spaceId),
    queryKey: USER_QUERY_KEYS.userSettings(isAuthenticated, spaceId, isSuperUser.data),
    staleTime: 5 * 60 * 1000,
  });

  return useMemo(() => query.data ?? defaultSettings, [query.data]);
};

export const useInitSettings = () =>
  useQuery({
    queryFn: () => fetchSettings(false, ''),
    queryKey: USER_QUERY_KEYS.userSettings(),
    refetchOnMount: false,
    retry: 3,
    retryDelay: (attempt) => attempt * 1000,
  });

export const useUserSettings = () => {
  const query = useQuery({
    queryFn: () => fetchSettings(true, ''),
    queryKey: USER_QUERY_KEYS.userSettings(true, ''),
    staleTime: 5 * 60 * 1000,
  });

  return useMemo(() => query.data ?? defaultSettings, [query.data]);
};
